import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useContactsDescriptionQuery from "app/pages/sales-intelligence/sub-modules/contacts-tab/queries/useContactsDescriptionQuery";

import DepartmentsDropdown from "../DepartmentsDropdown/DepartmentsDropdown";
import ExcludedTitles from "../ExcludedTitles/ExcludedTitles";
import IncludedTitles from "../IncludedTitles/IncludedTitles";
import SenioritiesDropdown from "../SenioritiesDropdown/SenioritiesDropdown";
import {
    StyledDescription,
    StyledFieldsContainer,
    StyledFieldWrap,
    StyledInput,
    StyledLabel,
    StyledLabelWrap,
    StyledModalInner,
    StyledModalTitle,
    StyledNameFieldWrap,
    StyledSection,
    StyledSectionTitle,
} from "../styles";

import type { ProfileContentProps } from "./type";

export const ProfileContent = ({
    onNameChange,
    onChangeDepartments,
    onChangeSeniorities,
    onChangeIncludedTitles,
    onChangeExcludedTitles,
    titleI18nKey,
    profileName,
    selectedDepartments,
    selectedSeniorities,
    includedTitles,
    excludedTitles,
    className,
    descriptionI18n,
    footerComponent,
}: ProfileContentProps) => {
    const { data: contactsDescription } = useContactsDescriptionQuery();

    const departments = contactsDescription?.employeeProfile.departments || [];
    const seniorities = contactsDescription?.employeeProfile.seniority || [];
    // https://stackoverflow.com/a/72399362
    // see article for explanation as to why each conditional JSX element is being wrapped in a fragment
    return (
        <StyledModalInner className={className}>
            <>
                {titleI18nKey ? (
                    <I18n
                        element={StyledModalTitle}
                        data-automation="title-text"
                        translatable={{ key: titleI18nKey }}
                    />
                ) : null}
                {descriptionI18n ? (
                    <I18n
                        element={StyledDescription}
                        data-automation="description-text"
                        translatable={{
                            key: descriptionI18n,
                        }}
                    />
                ) : null}
            </>
            <StyledNameFieldWrap>
                <StyledLabelWrap>
                    <I18n
                        element={StyledLabel}
                        htmlFor="profile-name-input"
                        data-automation="profile-name-label"
                        translatable={{
                            key: "si.account-overview.employee-profiles.name.label",
                        }}
                    />
                    <StyledInput
                        type="text"
                        value={profileName}
                        id="profile-name-input"
                        onChange={onNameChange}
                        placeholder={translate("si.account-overview.employee-profiles.name.placeholder")}
                    />
                </StyledLabelWrap>
            </StyledNameFieldWrap>
            <StyledSection>
                <I18n
                    element={StyledSectionTitle}
                    data-automation="section-label"
                    translatable={{
                        key: "si.account-overview.employee-profiles.basic-selection.text",
                    }}
                />
                <StyledFieldsContainer>
                    <StyledFieldWrap>
                        <StyledLabelWrap>
                            <I18n
                                element={StyledLabel}
                                data-automation="department-selection-label"
                                translatable={{
                                    key: "si.account-overview.employee-profiles.departments.label",
                                }}
                            />
                        </StyledLabelWrap>
                        <DepartmentsDropdown
                            width="100%"
                            items={departments}
                            onSelect={onChangeDepartments}
                            selectedIds={selectedDepartments}
                        />
                    </StyledFieldWrap>
                    <StyledFieldWrap>
                        <StyledLabelWrap>
                            <I18n
                                element={StyledLabel}
                                data-automation="seniority-selection-label"
                                translatable={{
                                    key: "si.account-overview.employee-profiles.seniorities.label",
                                }}
                            />
                        </StyledLabelWrap>
                        <SenioritiesDropdown
                            width="100%"
                            items={seniorities}
                            onSelect={onChangeSeniorities}
                            selectedIds={selectedSeniorities}
                        />
                    </StyledFieldWrap>
                </StyledFieldsContainer>
            </StyledSection>
            <StyledSection>
                <I18n
                    element={StyledSectionTitle}
                    data-automation="section-label"
                    translatable={{
                        key: "si.account-overview.employee-profiles.titles-selection.text",
                    }}
                />
                <StyledFieldsContainer>
                    <IncludedTitles
                        onChange={onChangeIncludedTitles}
                        selectedItems={includedTitles}
                        id="title-selection-input-include"
                    />
                    <ExcludedTitles
                        onChange={onChangeExcludedTitles}
                        selectedItems={excludedTitles}
                        id="title-selection-input-exclude"
                    />
                </StyledFieldsContainer>
            </StyledSection>
            <>{footerComponent ? footerComponent : <></>}</>
        </StyledModalInner>
    );
};
